'use client';

import { useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { LanguageSwitcher } from '@/components/layout/language-switcher';
import { BrandWordmark } from './brand-wordmark';

interface MobileNavProps {
  id: string;
  open: boolean;
  onClose: () => void;
  links: readonly { href: string; labelKey: string }[];
}

export function MobileNav({ id, open, onClose, links }: MobileNavProps) {
  const t = useTranslations();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      // Trap focus within drawer
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      closeButtonRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] md:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <div
        id={id}
        ref={drawerRef}
        role="dialog"
        aria-label={t('public.nav.navigationMenu')}
        aria-modal="true"
        className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Image src="/eifo-logo.png" alt="EiFO Academy" width={32} height={32} />
            <BrandWordmark className="text-lg" />
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
            onClick={onClose}
            aria-label={t('public.nav.closeNavigationMenu')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex flex-col px-2 py-4" aria-label={t('public.nav.mobileNavigation')}>
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={onClose}
              className="flex min-h-[48px] items-center rounded-md px-4 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-eifo-primary"
            >
              {t(link.labelKey)}
            </Link>
          ))}
        </nav>

        {/* Auth buttons — pushed to bottom via mt-auto in flex column */}
        <div className="mt-auto border-t px-4 py-4">
          <div className="mb-3 flex justify-center">
            <LanguageSwitcher variant="drawer" />
          </div>
          <Link
            href="/login"
            onClick={onClose}
            className="mb-2 block rounded-md border border-eifo-primary px-4 py-3 text-center text-sm font-medium text-eifo-primary hover:bg-eifo-primary/5"
          >
            {t('public.header.logIn')}
          </Link>
          <Link
            href="/register"
            onClick={onClose}
            className="block rounded-md bg-eifo-orange px-4 py-3 text-center text-sm font-medium text-white hover:bg-eifo-orange/90"
          >
            {t('public.header.register')}
          </Link>
        </div>
      </div>
    </div>
  );
}
